import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ApiCallError, callFunction } from '@/lib/api';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE } from '@/lib/typography';
import { useXdisSender } from './useXdisSender';
import { XdisReceiverChooser } from './XdisReceiverChooser';
import { XDIS91_TESTCENTER_EAN, chosenReceiver, receiverIssues, type ReceiverChoice } from '@/lib/xdis/xdisReceiver';

// Spor A block 3, T3.2 (XDIS-EPJ 5.3): "Besvar" a received XDIS91 within its episode.
// Communicate builds the answer from the stored XDIS91: the EpisodeOfCareIdentifier and the
// patient are carried over; the receiver is its Sender unless another is chosen (XDIS-EPJ 5.4:
// MedCom Testcenter, a SOR unit or a typed EAN). The XDIS91 must have an
// episode and a patient in this clinic; otherwise the answer is refused.

const SUBJECT_MAX = 70;
const TEXT_MAX = 3500;
const FIELD =
  'mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal   ';

type AnswerResult = { id: string; patientId: string; episodeOfCareIdentifier: string | null; receiver: { ean: string; displayName: string } };

export function Xdis91AnswerForm({ clinicId, exchangeId, subject, onClose }: { clinicId: string; exchangeId: string; subject: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { sender, gap } = useXdisSender(clinicId);
  const [priority, setPriority] = useState<'rutine' | 'høj_prioritet'>('rutine');
  const [subj, setSubj] = useState(subject ? `Sv: ${subject}`.slice(0, SUBJECT_MAX) : '');
  const [text, setText] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [receiverChoice, setReceiverChoice] = useState<ReceiverChoice>({ mode: 'default', party: null });
  const [attempted, setAttempted] = useState(false);
  const receiverProblems = receiverChoice.mode === 'default' ? [] : receiverIssues(receiverChoice.party);
  const invalid = !text.trim() ? t('patients.xdis.answer_text_missing', 'Skriv en tekst.') : null;

  async function onSubmit() {
    setAttempted(true);
    if (invalid || !sender || receiverProblems.length > 0) return;
    const receiver = receiverChoice.mode === 'default' ? null : chosenReceiver(receiverChoice, null);
    setPending(true);
    setError(null);
    try {
      const built = await callFunction<Record<string, unknown>, AnswerResult>('communicate-xdis', {
        action: 'answer91',
        clinic_id: clinicId,
        exchange_id: exchangeId,
        sender,
        ...(receiver ? { receiver } : {}),
        correspondence: { priority, ...(subj.trim() ? { subject: subj.trim() } : {}), text },
      });
      setResult(built);
      await queryClient.invalidateQueries({ queryKey: ['hospital-referral-outbox', clinicId, built.patientId] });
      await queryClient.invalidateQueries({ queryKey: ['communicate-xdis-flow', clinicId, built.patientId] });
      toast.success(t('referrals.reply_correspondence_built', 'Korrespondancen er dannet og ligger i udbakken.'));
    } catch (err) {
      const message = err instanceof ApiCallError ? err.message : t('referrals.reply_failed', 'Svaret kunne ikke dannes.');
      setError(message);
      toast.error(message);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-ds-line p-3" data-testid="xdis91-answer-form">
      <p className={FORM_HINT_TYPE}>
        {t('patients.xdis.answer_hint', 'Svaret dannes med samme forløbsnummer (EpisodeOfCareIdentifier) og patient. Modtageren er korrespondancens afsender, medmindre du vælger en anden.')}
      </p>
      <XdisReceiverChooser
        fieldId="xdis91-receiver"
        choice={receiverChoice}
        onChange={setReceiverChoice}
        defaultLabel={t('patients.xdis.receiver_default91', 'Korrespondancens afsender')}
        defaultParty={null}
        testcenterEan={XDIS91_TESTCENTER_EAN}
        showErrors={attempted}
      />
      {gap && <p role="alert" className={FORM_ERROR_TYPE}>{gap}</p>}
      <label className="block">
        <span className={FORM_LABEL_TYPE}>{t('referrals.reply_priority', 'Prioritet')}</span>
        <select className={FIELD} value={priority} onChange={(e) => setPriority(e.target.value as 'rutine' | 'høj_prioritet')}>
          <option value="rutine">{t('medcom.correspondence.priority_rutine', 'Rutine')}</option>
          <option value="høj_prioritet">{t('medcom.correspondence.priority_high', 'Høj prioritet')}</option>
        </select>
      </label>
      <label className="block">
        <span className={FORM_LABEL_TYPE}>{t('referrals.reply_subject', 'Emne')}</span>
        <input className={FIELD} maxLength={SUBJECT_MAX} value={subj} onChange={(e) => setSubj(e.target.value)} />
      </label>
      <label className="block">
        <span className={FORM_LABEL_TYPE}>{t('referrals.reply_text', 'Tekst')}</span>
        <textarea className={FIELD} rows={5} maxLength={TEXT_MAX} value={text} onChange={(e) => setText(e.target.value)} />
      </label>
      {error && <p role="alert" className={FORM_ERROR_TYPE}>{error}</p>}
      {result && (
        <p className="text-sm text-ds-green-ink" data-testid="xdis91-answer-result">
          {t('patients.xdis.answer_built', 'Svar dannet til {{receiver}} ({{ean}}), forløb {{episode}}.', {
            receiver: result.receiver.displayName || result.receiver.ean,
            ean: result.receiver.ean,
            episode: result.episodeOfCareIdentifier ?? '–',
          })}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="button" size="sm" onClick={() => void onSubmit()} disabled={pending || Boolean(gap) || Boolean(invalid) || Boolean(result)}>
          {pending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          {t('patients.xdis.answer_send', 'Dan svar')}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onClose}>
          {t('patients.xdis.close', 'Luk')}
        </Button>
      </div>
    </div>
  );
}
